import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { StatusCell } from './StatusCell';

import { ModernButton } from '../../components/ui/ModernButton';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FileUpload } from 'primereact/fileupload';
import { TabView, TabPanel } from 'primereact/tabview';
import { Chart } from 'primereact/chart';
import { InputSwitch } from 'primereact/inputswitch';
import { useAuth } from '../../contexts/AuthContext';
import { AgentService } from '../../services/agentService';
import { CustomerService } from '../../services/customerService';
import {
	ProfileImageService,
	MAX_PROFILE_IMAGE_BYTES,
	ALLOWED_PROFILE_IMAGE_TYPES,
	formatFileSize
} from '../../services/profileImageService';
import { InsightService } from '../../services/insightService';
import { Agent } from '../../types/agent';
import { getAgentInitials, getAgentDisplayName, formatAgentNameWithInitial } from '../../utils/stringHelpers';
import './AgentManagementPage.css';

interface AgentFormData {
	email: string;
	firstName: string;
	lastName: string;
	displayName: string;
	phone: string;
	companyName: string;
	role: 'agent' | 'master_agent' | 'super_agent';
	territory: string;
	licenseNumber: string;
	specialties: string[];
	isActive: boolean;
	profileImage?: string;
}

interface AgentStats {
	customersCount: number;
	activeInsights: number;
	conversionRate: number;
	lastLoginAt?: Date;
}

// Must stay stable - the DataTable persists its state under this key in sessionStorage.
const AGENTS_TABLE_STATE_KEY = 'agents-table';

// Limits and formatting now live with the upload service, so this page and
// ProfilePage cannot drift apart on what counts as too large.

export const AgentManagementPage: React.FC = () => {
	const { currentAgent } = useAuth();
	const toast = useRef<Toast>(null);

	const [agents, setAgents] = useState<Agent[]>([]);
	const [loading, setLoading] = useState(true);
	const [showAgentDialog, setShowAgentDialog] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
	const [agentStats, setAgentStats] = useState<Map<string, AgentStats>>(new Map());
	// Signed avatar URLs, fetched once per table load rather than once per row.
	const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
	const [uploadingImage, setUploadingImage] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeTab, setActiveTab] = useState(0);
	const [savingAgent, setSavingAgent] = useState(false);

	const [formData, setFormData] = useState<AgentFormData>({
		email: '',
		firstName: '',
		lastName: '',
		displayName: '',
		phone: '',
		companyName: '',
		role: 'agent',
		territory: '',
		licenseNumber: '',
		specialties: [],
		isActive: true
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	const roleOptions = [
		{ label: 'Agent', value: 'agent' },
		{ label: 'Master Agent', value: 'master_agent' },
		{ label: 'Super Agent', value: 'super_agent' }
	];

	const specialtyOptions = [
		{ label: 'First-Time Buyers', value: 'first_time_buyers' },
		{ label: 'Refinancing', value: 'refinancing' },
		{ label: 'Investment Properties', value: 'investment' },
		{ label: 'Commercial Lending', value: 'commercial' },
		{ label: 'VA Loans', value: 'va_loans' },
		{ label: 'FHA Loans', value: 'fha_loans' },
		{ label: 'Jumbo Loans', value: 'jumbo_loans' }
	];

	const loadAgents = useCallback(async () => {
		setLoading(true);
		try {
			// Customer counts come from one aggregate call rather than one
			// list-of-1000 per agent. With a dozen agents that was a dozen round
			// trips, each shipping a thousand customer records to the browser to
			// produce a single integer.
			const [agentsList, counts] = await Promise.all([
				AgentService.getAllAgents(),
				CustomerService.getCustomerCounts()
			]);

			const statsResults = await Promise.all(
				agentsList.map(async (agent) => ({
					id: agent.id,
					stats: await loadAgentStats(agent.id, counts.byAgent[agent.id] || 0)
				}))
			);

			const statsMap = new Map<string, AgentStats>();
			statsResults.forEach(({ id, stats }) => {
				statsMap.set(id, stats);
			});

			setAgents(agentsList);
			setAgentStats(statsMap);

			// Avatars for the whole table in one batched call.
			const urls = await ProfileImageService.getUrls(agentsList.map(agent => agent.id));
			setAvatarUrls(urls);

			if (counts.capped) {
				toast.current?.show({
					severity: 'warn',
					summary: 'Counts are partial',
					detail: 'There are more customers than this view scans, so counts shown are minimums.',
					life: 6000
				});
			}
		} catch (error) {

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load agents',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') {
			loadAgents();
		}
	}, [currentAgent, loadAgents]);

	const loadAgentStats = async (agentId: string, customersCount: number): Promise<AgentStats> => {
		try {
			const allInsights = await InsightService.getAgentInsights(agentId);

			// Active insights = not converted and not dismissed
			const activeStatuses = ['new', 'viewed', 'contacted', 'in_progress'];
			const activeInsights = allInsights.filter(
				insight => activeStatuses.includes(insight.status)
			);

			return {
				customersCount,
				activeInsights: activeInsights.length,
				conversionRate: allInsights.length > 0
					? (allInsights.filter(i => i.status === 'converted').length / allInsights.length) * 100
					: 0
			};
		} catch (error) {
			console.error(`[AgentStats] Error loading stats for agent ${agentId}:`, error);
			// The customer count came from the aggregate call and is still good
			// even when the insight query fails, so keep it rather than
			// reporting a zero the data does not support.
			return {
				customersCount,
				activeInsights: 0,
				conversionRate: 0
			};
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'Invalid email format';
		}

		if (!formData.firstName.trim()) {
			newErrors.firstName = 'First name is required';
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = 'Last name is required';
		}

		if (!formData.companyName.trim()) {
			newErrors.companyName = 'Company name is required';
		}

		if (formData.licenseNumber && !/^[A-Z0-9-]+$/i.test(formData.licenseNumber)) {
			newErrors.licenseNumber = 'Invalid license number format';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCreateAgent = async () => {
		if (!validateForm()) {
			toast.current?.show({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Please fix the errors in the form',
				life: 3000
			});
			return;
		}

		setSavingAgent(true);
		try {
			const agentData = {
				email: formData.email.trim().toLowerCase(), // Normalize email
				firstName: formData.firstName.trim(),
				lastName: formData.lastName.trim(),
				phone: formData.phone.trim(),
				companyName: formData.companyName.trim(),
				role: formData.role,
				territory: formData.territory?.trim(),
				licenseNumber: formData.licenseNumber?.trim(),
				specialties: formData.specialties
			};

			const result = await AgentService.createAgentWithInvitation(agentData);

			// Check if email was sent successfully
			if (result.emailSent) {
				toast.current?.show({
					severity: 'success',
					summary: 'Success',
					detail: result.message || 'Agent created and invitation email sent',
					life: 4000
				});
			} else {
				// Agent was created but email failed - show warning
				toast.current?.show({
					severity: 'warn',
					summary: 'Agent Created - Email Failed',
					detail: result.emailError
						? `Agent created but email failed: ${result.emailError}. Use "Resend Invite" to try again.`
						: 'Agent created but invitation email failed to send. Use "Resend Invite" to try again.',
					life: 8000
				});
			}

			setShowAgentDialog(false);
			resetForm();
			await loadAgents();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to create agent';
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: errorMessage,
				life: 3000
			});
		} finally {
			setSavingAgent(false);
		}
	};

	const handleUpdateAgent = async () => {
		if (!selectedAgent) return;

		if (!validateForm()) {
			toast.current?.show({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Please fix the errors in the form',
				life: 3000
			});
			return;
		}

		setSavingAgent(true);
		try {
			// Prepare update data - only include fields that can be updated
			const updateData: any = {
				firstName: formData.firstName,
				lastName: formData.lastName,
				displayName: formData.displayName,
				phone: formData.phone,
				// Set both company and companyName for backward compatibility
				companyName: formData.companyName,
				company: formData.companyName,
				role: formData.role,
				territory: formData.territory,
				licenseNumber: formData.licenseNumber,
				// Ensure specialties is always an array
				specialties: Array.isArray(formData.specialties) ? formData.specialties : [],
				isActive: formData.isActive
			};

			// profileImage is deliberately not written here. Photos live in Cloud
			// Storage and are set through uploadAgentProfileImage, which also
			// clears the legacy base64 field on the document.

			await AgentService.updateAgent(selectedAgent.id, updateData);

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Agent updated successfully',
				life: 3000
			});

			setShowAgentDialog(false);
			resetForm();
			await loadAgents();
		} catch (error) {
			console.error('Error updating agent:', error);
			const errorMessage = error instanceof Error ? error.message : 'Failed to update agent';
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: errorMessage,
				life: 3000
			});
		} finally {
			setSavingAgent(false);
		}
	};

	const handleDeleteAgent = (agent: Agent) => {
		confirmDialog({
			message: `Are you sure you want to delete ${getAgentDisplayName(agent)}? This action cannot be undone.`,
			header: 'Confirm Delete',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: async () => {
				try {
					await AgentService.deleteAgent(agent.id);
					await loadAgents();

					toast.current?.show({
						severity: 'success',
						summary: 'Success',
						detail: 'Agent deleted successfully',
						life: 3000
					});
				} catch (error) {

					toast.current?.show({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to delete agent',
						life: 3000
					});
				}
			}
		});
	};

	const handleResendInvitation = async (agent: Agent) => {
		try {
			const result = await AgentService.resendInvitation(agent.id);

			// Check if email was sent successfully
			if (result.emailSent) {
				toast.current?.show({
					severity: 'success',
					summary: 'Invitation Resent',
					detail: result.message || `Invitation email has been resent to ${agent.email}`,
					life: 4000
				});
			} else {
				// Invitation was updated but email failed
				toast.current?.show({
					severity: 'warn',
					summary: 'Email Failed',
					detail: result.emailError
						? `Invitation updated but email failed: ${result.emailError}`
						: 'Invitation updated but email failed to send. Please check email configuration.',
					life: 8000
				});
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation';
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: errorMessage,
				life: 3000
			});
			throw error; // Re-throw so StatusCell knows it failed
		}
	};

	const openEditDialog = (agent: Agent) => {
		setSelectedAgent(agent);

		// Normalize specialties - handle both string and array formats
		let normalizedSpecialties: string[] = [];
		if (agent.specialties) {
			if (Array.isArray(agent.specialties)) {
				normalizedSpecialties = agent.specialties;
			} else if (typeof agent.specialties === 'string') {
				// If it's a string, convert to array
				normalizedSpecialties = [agent.specialties];
			}
		}

		setFormData({
			email: agent.email,
			firstName: agent.firstName || '',
			lastName: agent.lastName || '',
			displayName: agent.displayName || '',
			phone: agent.phone || '',
			// Support both company and companyName for backward compatibility
			companyName: agent.companyName || agent.company || '',
			role: agent.role,
			territory: agent.territory || '',
			licenseNumber: agent.licenseNumber || '',
			specialties: normalizedSpecialties,
			isActive: agent.isActive,
			profileImage: agent.profileImage
		});
		setEditMode(true);
		setShowAgentDialog(true);
	};

	const resetForm = () => {
		setFormData({
			email: '',
			firstName: '',
			lastName: '',
			displayName: '',
			phone: '',
			companyName: '',
			role: 'agent',
			territory: '',
			licenseNumber: '',
			specialties: [],
			isActive: true
		});
		setErrors({});
		setEditMode(false);
		setSelectedAgent(null);
	};

	const handleImageUpload = async (event: { files: File[] }) => {
		const file = event.files[0];
		if (!file) return;

		// The image goes to Cloud Storage rather than into the agent document.
		// Base64 in Firestore meant a 2 MB photo became ~2.7 MB encoded, past the
		// 1 MiB document limit, and rode along on every read of this table.
		const problem = ProfileImageService.validate(file);
		if (problem) {
			toast.current?.show({
				severity: 'error',
				summary: 'Cannot use that image',
				detail: problem,
				life: 6000
			});
			return;
		}

		// A new agent has no document yet, so there is nowhere to attach the file.
		if (!selectedAgent?.id) {
			toast.current?.show({
				severity: 'info',
				summary: 'Save the agent first',
				detail: 'Create the agent, then reopen it to add a photo.',
				life: 5000
			});
			return;
		}

		setUploadingImage(true);
		try {
			const url = await ProfileImageService.upload(file, selectedAgent.id);
			if (url) {
				setAvatarUrls(prev => ({ ...prev, [selectedAgent.id]: url }));
			}
			toast.current?.show({
				severity: 'success',
				summary: 'Photo updated',
				life: 3000
			});
		} catch (error: any) {
			toast.current?.show({
				severity: 'error',
				summary: 'Upload failed',
				detail: error?.message || 'Could not upload that image.',
				life: 6000
			});
		} finally {
			setUploadingImage(false);
		}
	};

	const handleImageRemove = async () => {
		if (!selectedAgent?.id) {
			setFormData(prev => ({ ...prev, profileImage: undefined }));
			return;
		}

		try {
			await ProfileImageService.remove(selectedAgent.id);
			setAvatarUrls(prev => {
				const next = { ...prev };
				delete next[selectedAgent.id];
				return next;
			});
			setFormData(prev => ({ ...prev, profileImage: undefined }));
		} catch (error: any) {
			toast.current?.show({
				severity: 'error',
				summary: 'Could not remove photo',
				detail: error?.message || 'Please try again.',
				life: 5000
			});
		}
	};

	// Column templates
	const agentBodyTemplate = (rowData: Agent) => {
		return (
			<div className="agent-info">
				<Avatar
					// Signed Storage URL, falling back to any legacy base64 still
					// on the document until those are migrated.
					image={avatarUrls[rowData.id] || rowData.profileImage}
					label={!(avatarUrls[rowData.id] || rowData.profileImage) ? getAgentInitials(rowData) : undefined}
					size="large"
					shape="circle"
					className="agent-avatar"
				/>
				<div className="agent-details">
					<div className="agent-name">{getAgentDisplayName(rowData)}</div>
					<div className="agent-email">{rowData.email}</div>
					{rowData.phone && (
						<div className="agent-phone">{rowData.phone}</div>
					)}
					{rowData.licenseNumber && (
						<div className="agent-license">NMLS {rowData.licenseNumber}</div>
					)}
					{rowData.territory && (
						<div className="agent-territory">{rowData.territory}</div>
					)}
				</div>
			</div>
		);
	};

	const roleBodyTemplate = (rowData: Agent) => {
		const roleLabel = rowData.role === 'super_agent' ? 'Super Agent' :
			rowData.role === 'master_agent' ? 'Master Agent' : 'Agent';
		const roleClass = rowData.role === 'super_agent' ? 'role-super' :
			rowData.role === 'master_agent' ? 'role-master' : 'role-agent';
		return (
			<span className={`role-badge ${roleClass}`}>
				{roleLabel}
			</span>
		);
	};

	const statusBodyTemplate = (rowData: Agent) => {
		return <StatusCell agent={rowData} onResendInvitation={handleResendInvitation} />;
	};

	const statsBodyTemplate = (rowData: Agent) => {
		const stats = agentStats.get(rowData.id);
		if (!stats) return <span className="text-muted">—</span>;

		return (
			<div className="agent-stats-compact">
				<div className="stat-number">{stats.customersCount}</div>
			</div>
		);
	};

	const specialtiesBodyTemplate = (rowData: Agent) => {
		// Normalize specialties - handle both string and array formats (backwards compatibility)
		let specialtiesArray: string[] = [];
		if (rowData.specialties) {
			if (Array.isArray(rowData.specialties)) {
				specialtiesArray = rowData.specialties;
			} else if (typeof rowData.specialties === 'string') {
				// If it's a string, convert to array
				specialtiesArray = [rowData.specialties];
			}
		}

		if (specialtiesArray.length === 0) {
			return <span className="text-muted">—</span>;
		}

		// Format specialty label
		const formatSpecialty = (specialty: string) => {
			return specialty.replace(/_/g, ' ').split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ');
		};

		return (
			<div className="specialties-compact">
				{specialtiesArray.map((specialty, index) => (
					<span key={index} className="specialty-chip" title={formatSpecialty(specialty)}>
						{formatSpecialty(specialty)}
					</span>
				))}
			</div>
		);
	};

	const handleToggleAgentStatus = (agent: Agent) => {
		const action = agent.isActive ? 'inactivate' : 'reactivate';
		const actionText = agent.isActive ? 'inactivated' : 'reactivated';
		const agentName = getAgentDisplayName(agent);

		confirmDialog({
			message: agent.isActive
				? `Are you sure you want to inactivate ${agentName}? They will immediately lose access to the portal and any multi-factor enrollment will be cleared.`
				: `Are you sure you want to reactivate ${agentName}? They will regain access to the portal.`,
			header: agent.isActive ? 'Confirm Inactivate' : 'Confirm Reactivate',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: agent.isActive ? 'p-button-danger' : 'p-button-primary',
			acceptLabel: agent.isActive ? 'Inactivate' : 'Reactivate',
			accept: async () => {
				try {
					// Use the cloud function which handles MFA clearing when deactivating
					await AgentService.setAgentStatus(agent.id, !agent.isActive);

					toast.current?.show({
						severity: 'success',
						summary: 'Success',
						detail: `Agent ${actionText} successfully`,
						life: 3000
					});

					await loadAgents();
				} catch (error) {
					console.error(`Error ${action}ing agent:`, error);
					toast.current?.show({
						severity: 'error',
						summary: 'Error',
						detail: `Failed to ${action} agent`,
						life: 3000
					});
				}
			}
		});
	};

	const actionsBodyTemplate = (rowData: Agent) => {
		return (
			<div className="action-buttons">
				{rowData.isActive ? (
					<ModernButton
						variant="ghost"
						size="sm"
						icon="pi pi-ban"
						onClick={() => handleToggleAgentStatus(rowData)}
						disabled={rowData.id === currentAgent?.id}
						title="Inactivate agent"
					/>
				) : (
					<ModernButton
						variant="ghost"
						size="sm"
						icon="pi pi-check-circle"
						onClick={() => handleToggleAgentStatus(rowData)}
						title="Reactivate agent"
					/>
				)}
				<ModernButton
					variant="ghost"
					size="sm"
					icon="pi pi-pencil"
					onClick={() => openEditDialog(rowData)}
					title="Edit agent"
				/>
				<ModernButton
					variant="ghost"
					size="sm"
					icon="pi pi-trash"
					onClick={() => handleDeleteAgent(rowData)}
					disabled={rowData.id === currentAgent?.id}
					title="Delete agent"
				/>
			</div>
		);
	};

	const filteredAgents = agents.filter(agent => {
		if (!searchTerm) return true;
		const search = searchTerm.toLowerCase();
		const agentName = getAgentDisplayName(agent).toLowerCase();
		// Support both company and companyName for backward compatibility
		const companyName = (agent.companyName || agent.company || '').toLowerCase();
		return (
			agentName.includes(search) ||
			agent.email.toLowerCase().includes(search) ||
			companyName.includes(search)
		);
	});

	// Chart data for analytics
	const performanceChartData = {
		labels: agents.map(a => formatAgentNameWithInitial(a)),
		datasets: [
			{
				label: 'Customers',
				data: agents.map(a => agentStats.get(a.id)?.customersCount || 0),
				backgroundColor: 'rgba(52, 152, 219, 0.6)'
			},
			{
				label: 'Active Insights',
				data: agents.map(a => agentStats.get(a.id)?.activeInsights || 0),
				backgroundColor: 'rgba(46, 204, 113, 0.6)'
			}
		]
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'top' as const
			}
		},
		scales: {
			x: {
				ticks: {
					autoSkip: false,
					maxRotation: 45,
					minRotation: 45
				}
			}
		}
	};

	if (currentAgent?.role !== 'master_agent' && currentAgent?.role !== 'super_agent') {
		return (
			<div className="agent-management-page">
				<Card className="access-denied-card">
					<div className="access-denied-content">
						<i className="pi pi-lock" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
						<h3>Access Denied</h3>
						<p>Only Master Agents and Super Agents can access agent management.</p>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className="agent-management-page">
			<Toast ref={toast} />
			<ConfirmDialog />

			<div className="page-header">
				<h1>Agent Management</h1>
				<p>Manage your team of mortgage lending agents</p>
			</div>

			<TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
				<TabPanel header="All Agents">
					<Card className="agents-card">
						<div className="table-header">
							<div className="header-left">
								<span className="pi pi-search search-icon"></span>
								<InputText
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder="Search agents..."
									className="search-input"
								/>
							</div>
							<div className="header-right">
								<ModernButton
									variant="ghost"
									icon={loading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
									onClick={loadAgents}
									disabled={loading}
									title="Refresh agents"
								/>
								<ModernButton
									variant="primary"
									icon="pi pi-plus"
									onClick={() => {
										resetForm();
										setShowAgentDialog(true);
									}}
								>
									Add Agent
								</ModernButton>
							</div>
						</div>

						<DataTable
							value={filteredAgents}
							loading={loading}
							paginator
							rows={10}
							emptyMessage="No agents found."
							className="agents-table"
							dataKey="id"
							stateKey={AGENTS_TABLE_STATE_KEY}
						>
							<Column field="agent" header="AGENT" body={agentBodyTemplate} style={{ width: '280px' }} />
							<Column field="role" header="ROLE" body={roleBodyTemplate} style={{ width: '180px' }} />
							<Column field="specialties" header="SPECIALTIES" body={specialtiesBodyTemplate} style={{ width: '300px' }} />
							<Column field="stats" header="CUSTOMERS" body={statsBodyTemplate} style={{ width: '100px' }} />
							<Column field="isActive" header="STATUS" body={statusBodyTemplate} style={{ width: '160px' }} />
							<Column header="ACTIONS" body={actionsBodyTemplate} style={{ width: '100px' }} />
						</DataTable>
					</Card>
				</TabPanel>

				<TabPanel header="Analytics">
					<div className="analytics-container">
						<Card className="summary-card">
							<div className="card-header-with-description">
								<h3>Team Summary</h3>
								<p className="card-description">An overview of your team's composition and total customer base across all agents.</p>
							</div>
							<div className="summary-grid">
								<div className="summary-item">
									<div className="summary-value">{agents.length}</div>
									<div className="summary-label">Total Agents</div>
									<div className="summary-hint">All registered agents</div>
								</div>
								<div className="summary-item">
									<div className="summary-value">{agents.filter(a => a.isActive).length}</div>
									<div className="summary-label">Active Agents</div>
									<div className="summary-hint">Currently enabled accounts</div>
								</div>
								<div className="summary-item">
									<div className="summary-value">{agents.filter(a => a.role === 'master_agent' || a.role === 'super_agent').length}</div>
									<div className="summary-label">Master/Super Agents</div>
									<div className="summary-hint">Agents with admin privileges</div>
								</div>
								<div className="summary-item">
									<div className="summary-value">
										{Array.from(agentStats.values()).reduce((sum, stats) => sum + stats.customersCount, 0)}
									</div>
									<div className="summary-label">Total Customers</div>
									<div className="summary-hint">Combined across all agents</div>
								</div>
							</div>
						</Card>

						<Card className="chart-card">
							<div className="card-header-with-description">
								<h3>Agent Performance Comparison</h3>
								<p className="card-description">
									Compare workload distribution across your team. The blue bars show the number of customers assigned to each agent,
									while the green bars show their active insights (opportunities currently being worked). Use this chart to identify
									workload imbalances and ensure customers are distributed evenly across your team.
								</p>
							</div>
							<div className="chart-legend-description">
								<div className="legend-item">
									<span className="legend-color" style={{ backgroundColor: 'rgba(52, 152, 219, 0.6)' }}></span>
									<span><strong>Customers:</strong> Total customers assigned to the agent</span>
								</div>
								<div className="legend-item">
									<span className="legend-color" style={{ backgroundColor: 'rgba(46, 204, 113, 0.6)' }}></span>
									<span><strong>Active Insights:</strong> Open opportunities the agent is currently working on</span>
								</div>
							</div>
							<Chart
								type="bar"
								data={performanceChartData}
								options={chartOptions}
								style={{ height: '400px' }}
							/>
						</Card>
					</div>
				</TabPanel>
			</TabView>

			{/* Agent Dialog */}
			<Dialog
				visible={showAgentDialog}
				onHide={() => {
					setShowAgentDialog(false);
					resetForm();
				}}
				header={
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
						<span>{editMode ? 'Edit Agent' : 'Add New Agent'}</span>
						{editMode && (
							<div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginRight: '1.5rem' }}>
								<label 
									htmlFor="activeToggle" 
									style={{ 
										fontSize: '0.875rem', 
										fontWeight: 500,
										color: formData.isActive ? '#27ae60' : '#95a5a6',
										margin: 0 
									}}
								>
									{formData.isActive ? 'Active' : 'Inactive'}
								</label>
								<InputSwitch
									inputId="activeToggle"
									checked={formData.isActive}
									onChange={(e) => setFormData({ ...formData, isActive: e.value })}
								/>
							</div>
						)}
					</div>
				}
				style={{ width: '50vw' }}
				breakpoints={{ '960px': '75vw', '640px': '90vw' }}
				footer={
					<div className="modern-form-actions">
						<ModernButton variant="ghost" onClick={() => {
							setShowAgentDialog(false);
							resetForm();
						}}>
							Cancel
						</ModernButton>
						<ModernButton
							variant="primary"
							onClick={editMode ? handleUpdateAgent : handleCreateAgent}
							loading={savingAgent}
						>
							{editMode ? 'Update' : 'Create'}
						</ModernButton>
					</div>
				}
			>
				<div className="agent-form">
					{/* Top Section: Contact Info + Profile Photo */}
					<div className="form-top-section">
						<div className="contact-info-column">
							<div className="field">
								<label htmlFor="firstName">First Name *</label>
								<InputText
									id="firstName"
									value={formData.firstName}
									onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
									className={errors.firstName ? 'p-invalid' : ''}
								/>
								{errors.firstName && <small className="p-error">{errors.firstName}</small>}
							</div>

							<div className="field">
								<label htmlFor="lastName">Last Name *</label>
								<InputText
									id="lastName"
									value={formData.lastName}
									onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
									className={errors.lastName ? 'p-invalid' : ''}
								/>
								{errors.lastName && <small className="p-error">{errors.lastName}</small>}
							</div>

							<div className="field">
								<label htmlFor="email">Email *</label>
								<InputText
									id="email"
									type="email"
									value={formData.email}
									onChange={(e) => setFormData({ ...formData, email: e.target.value })}
									className={errors.email ? 'p-invalid' : ''}
									disabled={editMode}
								/>
								{errors.email && <small className="p-error">{errors.email}</small>}
							</div>

							<div className="field">
								<label htmlFor="phone">Phone</label>
								<InputMask
									id="phone"
									mask="(999) 999-9999"
									value={formData.phone}
									onChange={(e) => setFormData({ ...formData, phone: e.value || '' })}
									placeholder="(555) 123-4567"
								/>
							</div>
						</div>

						<div className="profile-photo-section">
							<label>Profile Photo</label>
							<div className="photo-upload-card">
								{(selectedAgent && avatarUrls[selectedAgent.id]) || formData.profileImage ? (
									<div className="photo-preview-container">
										<Avatar
											image={(selectedAgent && avatarUrls[selectedAgent.id]) || formData.profileImage}
											size="xlarge"
											shape="circle"
											className="profile-preview-large"
										/>
										<ModernButton
											variant="ghost"
											size="sm"
											icon="pi pi-times"
											onClick={handleImageRemove}
											className="remove-photo-btn"
											title="Remove photo"
										/>
									</div>
								) : (
									<div className="photo-placeholder">
										<i className="pi pi-user" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
									</div>
								)}
								<FileUpload
									mode="basic"
									accept={ALLOWED_PROFILE_IMAGE_TYPES.join(',')}
									maxFileSize={MAX_PROFILE_IMAGE_BYTES}
									onSelect={handleImageUpload}
									disabled={uploadingImage}
									chooseLabel={uploadingImage ? 'Uploading...' : ((selectedAgent && avatarUrls[selectedAgent.id]) || formData.profileImage ? 'Change Photo' : 'Upload Photo')}
									auto={false}
									className="photo-upload-btn"
								/>
								<small style={{ display: 'block', marginTop: '0.75rem', color: '#6b7280', textAlign: 'center', fontSize: '0.75rem' }}>
									JPG, PNG, GIF, WebP<br/>Max size: {formatFileSize(MAX_PROFILE_IMAGE_BYTES)}
								</small>
							</div>
						</div>
					</div>

					{/* Bottom Section: Other Fields */}
					<div className="form-grid">
						<div className="field">
							<label htmlFor="displayName">Display Name</label>
							<InputText
								id="displayName"
								value={formData.displayName}
								onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
								placeholder="Leave empty to use full name"
							/>
						</div>

						<div className="field">
							<label htmlFor="companyName">Company Name *</label>
							<InputText
								id="companyName"
								value={formData.companyName}
								onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
								className={errors.companyName ? 'p-invalid' : ''}
							/>
							{errors.companyName && <small className="p-error">{errors.companyName}</small>}
						</div>

						<div className="field">
							<label htmlFor="role">Role</label>
							<Dropdown
								id="role"
								value={formData.role}
								options={roleOptions}
								onChange={(e) => setFormData({ ...formData, role: e.value })}
								placeholder="Select Role"
							/>
						</div>

						<div className="field">
							<label htmlFor="territory">Territory</label>
							<InputText
								id="territory"
								value={formData.territory}
								onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
								placeholder="e.g., North Region"
							/>
						</div>

						<div className="field">
							<label htmlFor="licenseNumber">License Number</label>
							<InputText
								id="licenseNumber"
								value={formData.licenseNumber}
								onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
								className={errors.licenseNumber ? 'p-invalid' : ''}
								placeholder="e.g., NMLS-123456"
							/>
							{errors.licenseNumber && <small className="p-error">{errors.licenseNumber}</small>}
						</div>

						<div className="field">
							<label htmlFor="specialties">Specialties</label>
							<MultiSelect
								id="specialties"
								value={formData.specialties}
								options={specialtyOptions}
								onChange={(e) => setFormData({ ...formData, specialties: e.value })}
								placeholder="Select Specialties"
								display="comma"
								maxSelectedLabels={3}
							/>
						</div>

						{!editMode && (
							<div className="field full-width">
								<p style={{ color: '#7f8c8d', fontSize: '0.875rem', margin: 0, padding: '0.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
									<i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
									New agents will be inactive until they accept their invitation and complete registration.
								</p>
							</div>
						)}
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default AgentManagementPage;
